import { ModalSairSemSalvar } from "@/shared/components/ModalSairSemSalvar";
import { useProtecaoFormulario } from "@/shared/hooks/useProtecaoSaida";
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMinisterios } from "@/core/hooks/useMinisterios";
import { FormularioComunicado } from "@/modules/comunicados/components/FormularioComunicado";
import {
  useComunicado,
  useEditarComunicado,
  usePodeGerenciarComunicados,
} from "@/modules/comunicados/hooks/useComunicados";
import type {
  ComunicadoComRelacoes,
  DadosComunicado,
} from "@/modules/comunicados/services/comunicadosService";

const CLASSE_MAIN = "mx-auto w-full max-w-2xl px-4 pb-24 pt-6 sm:px-6 sm:pt-10";

function Aviso({ children }: { children: string }) {
  return (
    <main className={CLASSE_MAIN}>
      <p role="alert" className="rounded-xl border border-border bg-surface p-5 text-sm text-muted">
        {children}{" "}
        <Link to="/comunicados" className="font-bold text-primary hover:underline">
          Voltar aos comunicados
        </Link>
      </p>
    </main>
  );
}

/**
 * Conteúdo da edição. Guarda uma FOTO do comunicado no momento em que a tela
 * abriu (`useState` ignora props novas): a versão enviada à RPC é a que a
 * pessoa realmente viu. Se outra pessoa mexer enquanto ela digita, a RPC
 * recusa (conflito) em vez de sobrescrever em silêncio — e o texto digitado
 * não some por causa de um refetch em segundo plano.
 */
function EdicaoComunicado({ comunicadoInicial }: { comunicadoInicial: ComunicadoComRelacoes }) {
  const navigate = useNavigate();
  const [comunicado] = useState(comunicadoInicial);
  const { data: ministerios } = useMinisterios();
  const editar = useEditarComunicado();
  const [erro, setErro] = useState<string | null>(null);
  const protecao = useProtecaoFormulario();

  const opcoesMinisterio = (ministerios ?? [])
    .filter((m) => m.ativo)
    .map((m) => ({ id: m.id, nome: m.nome }));

  async function aoEnviar(dados: DadosComunicado) {
    setErro(null);
    try {
      await editar.mutateAsync({ id: comunicado.id, versao: comunicado.versao, dados });
      protecao.liberar();
      navigate("/comunicados", { state: { aviso: "Alterações salvas." } });
    } catch (e) {
      setErro(e instanceof Error ? e.message : "Não foi possível salvar as alterações.");
    }
  }

  return (
    <main className={CLASSE_MAIN}>
      <header className="mb-5">
        <h1 className="text-2xl font-black tracking-tight text-primary">Editar comunicado</h1>
        <p className="mt-1 text-sm text-muted">Ao editar, a contagem de 30 dias não recomeça.</p>
      </header>
      <div {...protecao.areaProps}>
        <FormularioComunicado
          valoresIniciais={comunicado}
          ministerios={opcoesMinisterio}
          carregando={editar.isPending}
          erro={erro}
          onSubmit={(dados) => void aoEnviar(dados)}
          onCancelar={() => navigate("/comunicados")}
        />
      </div>
      <ModalSairSemSalvar
        aberto={protecao.pedindoSaida}
        onSair={protecao.confirmarSaida}
        onContinuar={protecao.continuarEditando}
      />
    </main>
  );
}

/**
 * Edição de um comunicado publicado. "Não encontrado" e "sem permissão" têm a
 * MESMA resposta na tela (a RLS devolve nada nos dois casos).
 */
export function EditarComunicadoPage() {
  const { id } = useParams<{ id: string }>();
  const { data: podeGerenciar, isLoading: carregandoPermissao } = usePodeGerenciarComunicados();
  const { data: comunicado, isLoading, isError } = useComunicado(id);

  if (carregandoPermissao || isLoading) {
    return (
      <main className={CLASSE_MAIN}>
        <p className="text-sm text-muted">Carregando...</p>
      </main>
    );
  }
  if (!podeGerenciar)
    return <Aviso>Só quem lidera ou gerencia o elo Comunicados pode editar.</Aviso>;
  if (isError) return <Aviso>Não foi possível carregar o comunicado.</Aviso>;
  if (!comunicado) return <Aviso>Comunicado não encontrado.</Aviso>;
  if (comunicado.situacao !== "publicado") {
    return <Aviso>Só dá para editar comunicados publicados. Restaure o comunicado primeiro.</Aviso>;
  }

  return <EdicaoComunicado comunicadoInicial={comunicado} />;
}
